import { createSelector } from 'reselect';
import { parse } from 'qs';

import { eventMetaDataSelector } from '../tracking/selectors';
import { TRACKING_SUB_AREA } from '../../../trackingV2';
import { TEST_EMPTY_NAME_DEFAULT, TIME_INTERVAL_OPTIONS } from '../../../constants';
import { getTestCaseStatsConfig } from '../../../util';
import { getBarSelectionParams } from '../../../api-adapter';

export const routerLocation = (store) => store.router.location;

export const createGetNameParam = () => createSelector([
  routerLocation,
], (location) => (
  parse(location.search, { ignoreQueryPrefix: true })?.name
));

export const createGetPagination = (testCaseStore) => createSelector([
  testCaseStore,
], (testCase) => {
  const current = testCase.getIn(['pagination', 'current']);
  const size = testCase.getIn(['pagination', 'size']);
  const total = testCase.get('total');

  return {
    current,
    pages: Math.ceil(total / size),
    show: total > size,
    size,
  };
});

export const createGetParams = ({
  getPagination,
  getFiltersParams,
  getNameParam,
  testCaseStore,
}) => createSelector([
  getPagination,
  getFiltersParams,
  getNameParam,
  testCaseStore,
], (pagination, filtersParams, name, testCase) => ({
  ...filtersParams,
  ...(getBarSelectionParams(testCase.get('trends'))),
  name: name === TEST_EMPTY_NAME_DEFAULT ? '' : name,
  limit: pagination.size,
  offset: (pagination.current - 1) * pagination.size,
  sort: testCase.getIn(['sort', 'direction']),
  sort_by: testCase.getIn(['sort', 'field']),
}));

export const createTestCasesTrendsParamsSelector = ({
  getFiltersParams,
  getNameParam,
  testCaseStore,
}) => createSelector([
  getFiltersParams,
  getNameParam,
  testCaseStore,
], (filterParams, name, testCase) => {
  const key = testCase.getIn(['trends', 'interval']);

  return {
    ...filterParams,
    name: name === TEST_EMPTY_NAME_DEFAULT ? '' : name,
    groupby: TIME_INTERVAL_OPTIONS[key].value,
  };
});

export const createTestCaseSelector = ({
  testCaseStore,
  getPagination,
  getTestNameParam,
  source,
  productSubArea = TRACKING_SUB_AREA.TEST_OVERVIEW_TEST_CASE,
}) => createSelector([
  testCaseStore,
  getPagination,
  getTestNameParam,
  eventMetaDataSelector,
], (testCase, pagination, testName, eventMetaData) => ({
  tests: testCase.get('tests'),
  loading: testCase.get('loading'),
  initialized: testCase.get('initialized'),
  isEmpty: testCase.get('tests').isEmpty(),
  sort: testCase.get('sort'),
  pagination,
  statsConfig: getTestCaseStatsConfig(
    testCase.get('stats').set('total', testCase.get('total')),
  ),
  testName,
  eventMetaData: {
    ...eventMetaData,
    sku_type: source,
    product_sub_area: productSubArea,
  },
  trends: testCase.get('trends'),
}));

export const createGetUser = (testCaseStore) => createSelector([
  testCaseStore,
  (store, options) => options,
], (testCase, options = {}) => testCase.getIn(['users', options.userId]));
